import { createRouter, createWebHistory, type RouteRecordRaw, type RouteMeta } from 'vue-router';
import { useAuth } from '@/composables/useAuth';
import { useUserStore } from '@/stores/user';
import type { MenuItem } from '@/api';
import { dynamicRouteState } from './routerState';
import { useKeepAliveStore } from '@/stores/keepAlive';
import { CHANNEL_KEY_QYWX } from '@/constants/channelAuth';

/** 子路由配置（path 相对 MainLayout，不带前导斜杠） */
interface SubRouteConfig {
  path: string;
  name: string;
  component: () => Promise<unknown>;
  /** 路由 meta，如 fullPageScroll 标记该页改为整页滚动（详见 MainLayout 内容区 overflow 处理） */
  meta?: RouteMeta;
}

/** 菜单对应的路由条目：菜单页本身 + 该菜单下的内嵌详情/编辑页 */
interface MenuRouteEntry {
  main: SubRouteConfig;
  children?: SubRouteConfig[];
}

/** 企微 Agent 各配置页只能选企业微信渠道的账号 */
const WECOM_AGENT_META = { channelKeys: [CHANNEL_KEY_QYWX] };

/**
 * 菜单路径 → 路由配置 映射表（仅维护一份，避免静态路由 + 动态路由两套）
 * key 为带前导斜杠的菜单 path，与后端 menus 接口返回保持一致
 * children 用于挂在父菜单权限下的非菜单子页面（详情/编辑/分类等）
 */
const MENU_ROUTE_MAP: Record<string, MenuRouteEntry> = {
  /** 数据看板目录：各业务数据独立看板 */
  '/dashboard/account': {
    main: {
      path: 'dashboard/account',
      name: 'DashboardAccount',
      component: () => import('@/views/dashboard/account/index.vue'),
      meta: { keepAlive: true },
    },
  },
  '/dashboard/reception': {
    main: {
      path: 'dashboard/reception',
      name: 'DashboardReception',
      component: () => import('@/views/dashboard/reception/index.vue'),
      meta: { keepAlive: true },
    },
  },
  '/dashboard/guide': {
    main: {
      path: 'dashboard/guide',
      name: 'DashboardGuide',
      component: () => import('@/views/dashboard/guide/index.vue'),
      meta: { keepAlive: true },
    },
  },
  '/dashboard/leads': {
    main: {
      path: 'dashboard/leads',
      name: 'DashboardLeads',
      component: () => import('@/views/dashboard/leads/index.vue'),
      meta: { keepAlive: true },
    },
  },
  '/dashboard/allocation': {
    main: {
      path: 'dashboard/allocation',
      name: 'DashboardAllocation',
      component: () => import('@/views/dashboard/allocation/index.vue'),
      meta: { keepAlive: true },
    },
  },
  '/dashboard/qiwei_billboard': {
    main: {
      path: 'dashboard/qiwei_billboard',
      name: 'DashboardQiweiBillboard',
      component: () => import('@/views/dashboard/qiweiBillboard/index.vue'),
      meta: { keepAlive: true },
    },
  },
  '/roles': {
    main: {
      path: 'roles',
      name: 'Roles',
      component: () => import('@/views/basicConfig/roles/index.vue'),
      meta: { keepAlive: true },
    },
  },
  '/system-config': {
    main: {
      path: 'system-config',
      name: 'SystemConfig',
      component: () => import('@/views/basicConfig/systemConfig/index.vue'),
    },
  },
  '/operation-log': {
    main: {
      path: 'operation-log',
      name: 'OperationLog',
      component: () => import('@/views/basicConfig/operationLog/index.vue'),
      meta: { keepAlive: true },
    },
  },
  '/push': {
    main: {
      path: 'push',
      name: 'Push',
      component: () => import('@/views/push/index.vue'),
      meta: { keepAlive: true },
    },
  },
  '/agent-personnel': {
    main: {
      path: 'agent-personnel',
      name: 'AgentPersonnel',
      component: () => import('@/views/basicConfig/agentPersonnel/index.vue'),
      meta: { keepAlive: true },
    },
    children: [
      {
        path: 'agent-personnel/group/:id/settings',
        name: 'AgentGroupSettings',
        component: () => import('@/views/basicConfig/agentPersonnel/GroupSettings.vue'),
      },
    ],
  },
  '/channel-materials': {
    main: {
      path: 'channel-materials',
      name: 'ChannelMaterials',
      component: () => import('@/views/marketingManagement/channelMaterials/index.vue'),
      meta: { keepAlive: true },
    },
  },
  '/channel-auth': {
    main: {
      path: 'channel-auth',
      name: 'ChannelAuthOverview',
      component: () => import('@/views/channelManagement/ChannelAuthOverview.vue'),
    },
    children: [
      {
        path: 'channel-auth/douyin',
        name: 'ChannelAuthDouyin',
        component: () => import('@/views/channelManagement/DouyinAuth.vue'),
      },
      {
        path: 'channel-auth/wechat-official',
        name: 'ChannelAuthWechatOfficial',
        component: () => import('@/views/channelManagement/WechatOfficialAuth.vue'),
      },
      {
        path: 'channel-auth/wechat-service',
        name: 'ChannelAuthWechatService',
        component: () => import('@/views/channelManagement/wechatService/WechatServiceAuth.vue'),
      },
      {
        path: 'channel-auth/qywx',
        name: 'ChannelAuthQywx',
        component: () => import('@/views/channelManagement/QywxAuth.vue'),
      },
      {
        path: 'channel-auth/dy-feige',
        name: 'ChannelAuthDyFeige',
        component: () => import('@/views/channelManagement/DyFeigeAuth.vue'),
      },
      {
        path: 'channel-auth/xiaohongshu',
        name: 'ChannelAuthXiaohongshu',
        component: () => import('@/views/channelManagement/XiaohongshuAuth.vue'),
      },
      {
        path: 'channel-auth/kuaishou',
        name: 'ChannelAuthKuaishou',
        component: () => import('@/views/channelManagement/KuaishouAuth.vue'),
      },
      {
        path: 'channel-auth/website',
        name: 'ChannelAuthWebsite',
        component: () => import('@/views/channelManagement/WebsiteConsultation.vue'),
      },
      {
        path: 'channel-auth/website/add',
        name: 'ChannelAuthWebsiteAdd',
        component: () =>
          import('@/views/channelManagement/WebsiteConsultation/AddWebSessionPage.vue'),
      },
      {
        path: 'channel-auth/website/edit/:accountId',
        name: 'ChannelAuthWebsiteEdit',
        component: () =>
          import('@/views/channelManagement/WebsiteConsultation/AddWebSessionPage.vue'),
      },
      {
        path: 'channel-auth/tencent',
        name: 'ChannelAuthTencent',
        component: () => import('@/views/channelManagement/TencentConsultation.vue'),
      },
      {
        path: 'channel-auth/baidu',
        name: 'ChannelAuthBaidu',
        component: () => import('@/views/channelManagement/BaiduMarketing.vue'),
      },
    ],
  },
  '/channel-ad-report': {
    main: {
      path: 'channel-ad-report',
      name: 'ChannelAdReport',
      component: () => import('@/views/channelManagement/adReporting/index.vue'),
      meta: { keepAlive: true },
    },
  },
  '/channel-customer-report': {
    main: {
      path: 'channel-customer-report',
      name: 'ChannelCustomerReport',
      component: () => import('@/views/channelManagement/customerReporting/index.vue'),
      meta: { keepAlive: true },
    },
  },
  '/channelManagement/autoReporting': {
    main: {
      path: 'channelManagement/autoReporting',
      name: 'AutoReporting',
      component: () => import('@/views/channelManagement/autoReporting/index.vue'),
      meta: { keepAlive: true },
    },
  },
  '/chat-records': {
    main: {
      path: 'chat-records',
      name: 'ChatRecords',
      component: () => import('@/views/chatRecords/index.vue'),
      meta: { fullPageScroll: true, keepAlive: true },
    },
  },
  '/session/workbench': {
    main: {
      path: 'session/workbench',
      name: 'SessionWorkbench',
      component: () => import('@/views/session/workbench/index.vue'),
      meta: { standalone: true },
    },
  },
  '/session/immediate': {
    main: {
      path: 'session/immediate',
      name: 'SessionImmediate',
      component: () => import('@/views/session/visitorSessions/index.vue'),
    },
  },
  '/manual-reply': {
    main: {
      path: 'manual-reply',
      name: 'ManualReply',
      component: () => import('@/views/marketingManagement/manualReply/index.vue'),
      meta: { keepAlive: true },
    },
  },
  '/marketing/tags': {
    main: {
      path: 'marketing/tags',
      name: 'MarketingTags',
      component: () => import('@/views/marketingManagement/tags/index.vue'),
      meta: { keepAlive: true },
    },
  },
  '/assetManagement/material': {
    main: {
      path: 'assetManagement/material',
      /** 菜单从「营销管理」挪到「素材管理」下，路由 name 保持历史值：别处按 name 跳转 */
      name: 'MarketingMaterials',
      component: () => import('@/views/assetManagement/material/index.vue'),
      meta: { keepAlive: true },
    },
  },
  '/assetManagement/gallery': {
    main: {
      path: 'assetManagement/gallery',
      name: 'AssetGallery',
      component: () => import('@/views/assetManagement/gallery/index.vue'),
      meta: { keepAlive: true },
    },
  },
  '/marketing/reach-plans': {
    main: {
      path: 'marketing/reach-plans',
      name: 'MarketingReachPlans',
      component: () => import('@/views/marketingManagement/reachPlans/index.vue'),
      meta: { keepAlive: true },
    },
  },
  '/agent/clue': {
    main: {
      path: 'agent/clue',
      name: 'Robots',
      component: () => import('@/views/agent/clue/index.vue'),
      meta: { keepAlive: true },
    },
    children: [
      {
        path: 'agent/clue/:id/edit',
        name: 'RobotEdit',
        component: () => import('@/views/agent/clue/RobotEdit.vue'),
        meta: { flushContentTop: true },
      },
      {
        path: 'agent/clue/:id/wake/new',
        name: 'RobotWakeCreate',
        component: () => import('@/views/agent/robotEdit/RobotWakeEditPage.vue'),
        // 落地页被线索 / 导购共用，meta 指明「返回」回到哪个编辑页
        meta: { robotEditRoute: 'RobotEdit' },
      },
      {
        path: 'agent/clue/:id/wake/edit/:groupId',
        name: 'RobotWakeEdit',
        component: () => import('@/views/agent/robotEdit/RobotWakeEditPage.vue'),
        meta: { robotEditRoute: 'RobotEdit' },
      },
      {
        path: 'agent/clue/:id/knowledge/edit/:docId',
        name: 'RobotKnowledgeDocEdit',
        component: () => import('@/views/agent/clue/KnowledgeDocEditPage.vue'),
      },
    ],
  },
  /** 导购Agent：列表页 + 编辑页，唤醒语落地页与线索Agent 共用 */
  '/agent/guide': {
    main: {
      path: 'agent/guide',
      name: 'GuideAgents',
      component: () => import('@/views/agent/guide/index.vue'),
      meta: { keepAlive: true },
    },
    children: [
      {
        path: 'agent/guide/:id/edit',
        name: 'GuideAgentEdit',
        component: () => import('@/views/agent/guide/RobotEdit.vue'),
      },
      {
        path: 'agent/guide/:id/wake/new',
        name: 'GuideWakeCreate',
        component: () => import('@/views/agent/robotEdit/RobotWakeEditPage.vue'),
        meta: { robotEditRoute: 'GuideAgentEdit' },
      },
      {
        path: 'agent/guide/:id/wake/edit/:groupId',
        name: 'GuideWakeEdit',
        component: () => import('@/views/agent/robotEdit/RobotWakeEditPage.vue'),
        meta: { robotEditRoute: 'GuideAgentEdit' },
      },
    ],
  },
  /**
   * 企微Agent：设计上是「机器人列表 → 机器人配置页」，列表页尚未开发，
   * 故本入口直接落在配置页；发朋友圈 / 批量加好友作为「营销工具」tab 的二级页内嵌。
   * key 与 path 需与后端菜单下发的 path 完全一致（含大小写），否则注册不上路由并被判越权。
   */
  '/agent/wecom': {
    main: {
      path: 'agent/wecom',
      name: 'WeComAgent',
      component: () => import('@/views/agent/wecom/index.vue'),
      meta: { keepAlive: true },
    },
    children: [
      {
        path: 'agent/wecom/:id/edit',
        name: 'WeComAgentEdit',
        component: () => import('@/views/agent/wecom/RobotEdit.vue'),
        meta: WECOM_AGENT_META,
      },
      {
        path: 'agent/wecom/:id/wake/new',
        name: 'WeComWakeCreate',
        component: () => import('@/views/agent/robotEdit/RobotWakeEditPage.vue'),
        meta: { robotEditRoute: 'WeComAgentEdit', ...WECOM_AGENT_META },
      },
      {
        path: 'agent/wecom/:id/wake/edit/:groupId',
        name: 'WeComWakeEdit',
        component: () => import('@/views/agent/robotEdit/RobotWakeEditPage.vue'),
        meta: { robotEditRoute: 'WeComAgentEdit', ...WECOM_AGENT_META },
      },
      // 发朋友圈的新建 / 编辑落地页沿用原路径，收藏夹与历史链接不断；
      // 来源机器人靠 query.from 传递，不带则保存后回列表页
      {
        path: 'agent/wecom/tools/moments/create',
        name: 'WeComMomentCreate',
        component: () => import('@/views/agent/wecom/tools/moments/MomentPlanFormPage.vue'),
      },
      {
        path: 'agent/wecom/tools/moments/:id/edit',
        name: 'WeComMomentEdit',
        component: () => import('@/views/agent/wecom/tools/moments/MomentPlanFormPage.vue'),
      },
    ],
  },
  '/agent/live': {
    main: {
      path: 'agent/live',
      name: 'Live',
      component: () => import('@/views/agent/live/index.vue'),
      meta: { keepAlive: true },
    },
  },
};

/**
 * 菜单改版过渡：后端 menus 若仍下发老 path，归一到新路径。
 * 否则菜单未同步（灰度 / 缓存 / 回滚）时该模块既注册不上路由、又会被判越权跳到 /no-permission。
 * 后端菜单稳定后可删。
 */
const LEGACY_MENU_PATH_MAP: Record<string, string> = {
  '/robots': '/agent/clue',
  // 直播 Agent 改版过渡：原 7 个独立子菜单归一到单入口 /agent/live
  '/live/live-review': '/agent/live',
  '/live/new-fans': '/agent/live',
  '/live/bullet-chat': '/agent/live',
  '/live/live-customer': '/agent/live',
  '/live/smart-reply': '/agent/live',
  '/live/short-video': '/agent/live',
  '/live/live-stat': '/agent/live',
  // 企微两个工具菜单合并为「企微Agent」单入口
  '/agent/wecom/tools/moments': '/agent/wecom',
  '/agent/wecom/tools/addFriends': '/agent/wecom',
  // 素材列表从「营销管理」挪到「素材管理」下，老菜单与收藏的旧链接继续可用
  '/marketing/materials': '/assetManagement/material',
  // 数据看板改为目录，原看板页挪到「账号数据」
  '/dashboard': '/dashboard/account',
};

const normalizeMenuPath = (p: string): string => {
  const withSlash = p.startsWith('/') ? p : `/${p}`;
  return LEGACY_MENU_PATH_MAP[withSlash] ?? withSlash;
};

/** 根据用户菜单动态注册有权限的子路由（含菜单下的详情/编辑页） */
function addMenuRoutes(menus: MenuItem[]) {
  if (dynamicRouteState.added) return;
  // 多个老菜单 path 可能归一到同一个新入口（见 LEGACY_MENU_PATH_MAP），去重避免重复注册
  const registered = new Set<string>();
  const register = (entry: MenuRouteEntry) => {
    if (registered.has(entry.main.name)) return;
    registered.add(entry.main.name);
    if (entry.main.meta?.standalone) {
      // 独立全屏页不挂 MainLayout，作为顶层路由注册
      router.addRoute({ ...entry.main, path: `/${entry.main.path}` } as RouteRecordRaw);
    } else {
      router.addRoute('MainLayout', entry.main as RouteRecordRaw);
    }
    dynamicRouteState.paths.push(`/${entry.main.path}`);
    entry.children?.forEach((c) => {
      router.addRoute('MainLayout', c as RouteRecordRaw);
    });
  };
  const walk = (items: MenuItem[]) => {
    for (const item of items) {
      if (item.type === 2 && item.path) {
        const entry = MENU_ROUTE_MAP[normalizeMenuPath(item.path)];
        if (entry) register(entry);
      }
      if (item.children?.length) walk(item.children);
    }
  };
  walk(menus);
  dynamicRouteState.added = true;
}

/** 不受菜单权限限制、登录后默认可访问的路径 */
const PATH_PERMISSION_WHITELIST = ['/no-permission'];

/** 收集所有叶子菜单的 path（已规范化为带前导斜杠） */
function collectMenuPaths(menus: MenuItem[]): string[] {
  const result: string[] = [];
  const walk = (items: MenuItem[]) => {
    for (const item of items) {
      if (item.type === 2 && item.path) result.push(normalizeMenuPath(item.path));
      if (item.children?.length) walk(item.children);
    }
  };
  walk(menus);
  return result;
}

/** 目标路径是否在用户菜单授权范围内（菜单本身或它的子路径，如详情/编辑页） */
function isPathAllowed(path: string, menuPaths: string[]): boolean {
  if (PATH_PERMISSION_WHITELIST.includes(path)) return true;
  return menuPaths.some((mp) => path === mp || path.startsWith(`${mp}/`));
}

/** 所有菜单基路径（MENU_ROUTE_MAP 的 key，均带前导斜杠） */
const MENU_BASE_PATHS = Object.keys(MENU_ROUTE_MAP);

/**
 * 将任意路由路径归约到其所属菜单的基路径
 * 例如 /agent/clue/8/edit → /agent/clue，/marketing/tags → /marketing/tags
 * 用于登出时把内嵌详情/编辑页的 redirect 收敛到菜单页，
 * 避免新账号无对应数据时进入明细页触发接口报错；匹配不到（如 /no-permission）返回 null
 */
export function resolveMenuBasePath(path: string): string | null {
  let matched: string | null = null;
  for (const base of MENU_BASE_PATHS) {
    if (path === base || path.startsWith(`${base}/`)) {
      if (!matched || base.length > matched.length) matched = base;
    }
  }
  return matched;
}

/** 目标路径（可带 query/hash）是否在给定菜单授权范围内，供登录后校验 redirect 是否可用 */
export function isPathAuthorized(target: string, menus: MenuItem[]): boolean {
  const path = target.split(/[?#]/)[0] ?? target;
  return isPathAllowed(path, collectMenuPaths(menus));
}

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/Login.vue'),
    meta: { public: true },
  },
  {
    path: '/web-chat',
    name: 'WebChat',
    component: () => import('@/views/webChat/index.vue'),
    meta: { public: true },
  },
  {
    path: '/chatRecord',
    name: 'ChatRecord',
    component: () => import('@/views/outchatRecord/index.vue'),
    meta: { public: true },
  },
  { path: '/', redirect: '/dashboard/account' },
  { path: '/app.html', redirect: '/dashboard/account' },
  /** 菜单改版：「数据看板」改为目录，原看板页改名「账号数据」，老链接重定向 */
  { path: '/dashboard', redirect: '/dashboard/account' },
  /** 菜单改版：「机器人」并入「智能体」改名为「线索Agent」，老链接（收藏夹 / 历史记录）重定向到新路径 */
  { path: '/robots', redirect: '/agent/clue' },
  /**
   * 企微两个工具页原先是「企微Agent」单页下的营销工具 tab，现已随配置页下沉到
   * /agent/wecom/:id/edit。老链接没有 robotId，定位不到具体机器人，统一落到列表页。
   */
  {
    path: '/agent/wecom/tools/moments',
    redirect: '/agent/wecom',
  },
  {
    path: '/agent/wecom/tools/addFriends',
    redirect: '/agent/wecom',
  },
  {
    path: '/robots/:rest(.*)',
    redirect: (to) => `/agent/clue/${to.params.rest}`,
  },
  /** 菜单改版：「直播营销」并入「智能体」改名为「直播Agent」，老链接按 tab 重定向到单入口 */
  { path: '/live/live-review', redirect: '/agent/live?tab=live-review' },
  { path: '/live/new-fans', redirect: '/agent/live?tab=new-fans' },
  { path: '/live/bullet-chat', redirect: '/agent/live?tab=bullet-chat' },
  { path: '/live/live-customer', redirect: '/agent/live?tab=live-customer' },
  { path: '/live/smart-reply', redirect: '/agent/live?tab=smart-reply' },
  { path: '/live/short-video', redirect: '/agent/live?tab=short-video' },
  { path: '/live/live-stat', redirect: '/agent/live?tab=live-stat' },
  {
    path: '/',
    name: 'MainLayout',
    component: () => import('@/layouts/MainLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: 'no-permission',
        name: 'NoPermission',
        component: () => import('@/views/NoPermission.vue'),
      },
    ],
  },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach(async (to, from, next) => {
  /**
   * 一键登录票据只在应用入口消费一次（见 utils/ticketParam），路由层不该再带着它。
   * 入口虽已 replaceState 抹掉地址栏，但 vue-router 首次导航用的是它自己捕获的初始地址，
   * 仍会带上 ticket 并顺着 redirect 参数传播，这里统一剔除。
   */
  if (to.query.ticket !== undefined) {
    const query = { ...to.query };
    delete query.ticket;
    next({ path: to.path, query, hash: to.hash, replace: true });
    return;
  }

  const token = localStorage.getItem('token');

  // 公开路由
  if (to.meta.public) {
    if (token && to.name === 'Login') {
      try {
        await useAuth().initAuth();
        const userStore = useUserStore();
        const firstPath = userStore.firstMenuPath;
        const requested = typeof to.query.redirect === 'string' ? to.query.redirect : '';
        // redirect 不在新账号菜单权限内时回落到首个菜单，避免越权/接口报错
        const redirect =
          requested && isPathAuthorized(requested, userStore.menus)
            ? requested
            : firstPath || '/no-permission';
        next({ path: redirect, replace: true });
      } catch {
        useUserStore().logout();
        next();
      }
      return;
    }
    next();
    return;
  }

  // 未登录访问任何非公开路由（含未注册的动态菜单路由）：跳登录页
  if (!token) {
    next({ name: 'Login', query: { redirect: to.fullPath } });
    return;
  }

  // 已登录场景：确保鉴权信息和动态路由已就绪后再做权限判断
  if (token) {
    await useAuth()
      .initAuth()
      .catch(() => undefined);
    const userStore = useUserStore();
    // 仅在已拿到菜单时才注册动态路由并标记 added。
    // 若 initAuth 失败/超时（首次打开、后端冷启动时易发）导致 menus 为空，
    // 绝不能在此标记 added：否则后续 initAuth 成功拿到 menus 后会因 added 已为 true 跳过注册，
    // 真实菜单路由永久缺失 → 跳转后匹配不到路由白屏。留待下次导航重试注册。
    if (!dynamicRouteState.added && userStore.menus.length > 0) {
      addMenuRoutes(userStore.menus);
      /**
       * 动态路由注册后，需重新触发匹配。
       * query / hash 必须显式带上：传 { path } 时 vue-router 只认 path 部分，
       * 把 fullPath 里的 ?a=b 当路径处理后丢弃，深链（?tab= 、?session_id= 等）会在冷启动时失效。
       */
      next({ path: to.path, query: to.query, hash: to.hash, replace: true });
      return;
    }

    // 无任何菜单权限，或鉴权信息尚未就绪（menus 为空）：先到无权限页，
    // 待 MainLayout 重试 initAuth 成功拿到 menus 后，由其 watch 兜底跳转首个菜单，避免白屏
    if (userStore.menus.length === 0) {
      if (to.path !== '/no-permission') {
        next({ path: '/no-permission', replace: true });
        return;
      }
      next();
      return;
    }
    // 登录后时序兜底：当前在 /no-permission 但实际有菜单权限时跳到首个菜单
    // unauthorized 标记表示由越权拦截跳来，不做恢复跳转，保留无权限提示
    if (to.path === '/no-permission' && to.query.reason !== 'unauthorized') {
      const firstPath = userStore.firstMenuPath;
      if (firstPath) {
        next({ path: firstPath, replace: true });
        return;
      }
    }
    // 目标路径不在菜单授权范围内（含通过 URL 直接访问的兜底拦截）：跳无权限页
    if (!isPathAllowed(to.path, collectMenuPaths(userStore.menus))) {
      next({ path: '/no-permission', query: { reason: 'unauthorized' }, replace: true });
      return;
    }
    // 独立全屏页从后台内部跳转（点菜单、标签等）时新标签页打开，后台停在原页
    if (to.meta.standalone && from.matched.some((r) => r.name === 'MainLayout')) {
      window.open(router.resolve(to.fullPath).href, '_blank');
      next(false);
      return;
    }
  }

  next();
});

/**
 * 页面缓存注册：路由 meta.keepAlive 为 true 时把路由 name 加入 KeepAlive 的 include 列表，
 * 使该页面切走后保留筛选条件、分页、列表数据（配合 MainLayout 的 <KeepAlive> 与滚动位置恢复）。
 *
 * 接入新页面两步：
 * 1. 该路由 meta 加 keepAlive: true
 * 2. 页面组件加 defineOptions({ name: '<与路由 name 完全一致>' })
 *
 * 第 2 步不可省：本项目页面文件基本都叫 index.vue，<script setup> 自动推导出的组件名会全是
 * index 互相串号，而 KeepAlive 的 include 匹配的正是组件名，漏写会让缓存静默失效。
 * 故开发环境下校验组件名与路由 name 是否一致，不一致时告警。
 */
router.afterEach((to) => {
  if (!to.meta.keepAlive || typeof to.name !== 'string') return;
  if (import.meta.env.DEV) {
    const component = to.matched[to.matched.length - 1]?.components?.default as
      | { name?: string; __name?: string }
      | undefined;
    /** SFC 必定带 __name（文件名推导），取不到说明是这里的内省失效而非页面漏配，不误报 */
    const componentName = component?.name ?? component?.__name;
    if (componentName && componentName !== to.name) {
      console.warn(
        `[keepAlive] 路由「${to.name}」已标记 meta.keepAlive，但组件名为「${componentName}」，` +
          `两者必须一致缓存才会生效，请在页面中补充 defineOptions({ name: '${to.name}' })`,
      );
    }
  }
  useKeepAliveStore().addCache(to.name);
});
